/**
 * ScoreCalculator.ts - Score calculation for DEFCON TERMINAL
 *
 * Calculates player scores based on decisions, path modifiers, outcome type,
 * bonuses, and penalties.
 */

import type {
  Scenario,
  ScenarioNode,
  EndingNode,
  OutcomeType,
  PathType,
  EasterEgg,
  ScoreInfo,
} from '@shared/types';
import type { ScoreBreakdown, BonusType, PenaltyType } from '@shared/types';
import {
  BONUSES,
  PENALTIES,
  PATH_MODIFIERS,
  calculateComplexityMultiplier,
  getOutcomeMultiplier,
} from '@shared/types';
import { SCORING } from '@shared/constants';
import type { DecisionTree, NodeVisit, TreeStats } from './DecisionTree';

/**
 * Decision score info
 */
export interface DecisionScore {
  nodeId: string;
  choiceId: string;
  basePoints: number;
  tags: string[];
  multiplier: number;
}

/**
 * Score calculation result with detailed breakdown
 */
export interface CalculationResult {
  breakdown: ScoreBreakdown;
  decisionScores: DecisionScore[];
  applicableBonuses: BonusType[];
  applicablePenalties: { type: PenaltyType; count?: number }[];
  stats: TreeStats;
}

/**
 * Score calculation options
 */
export interface ScoreOptions {
  includeEasterEggs?: boolean;
  includeSpeedBonus?: boolean;
  playTimeSeconds?: number;
  casualties?: number;
  warheadsUsed?: number;
  discoveredEasterEggs?: string[];
}

/**
 * ScoreCalculator class - Calculates game scores
 */
export class ScoreCalculator {
  private scenario: Scenario | null = null;

  /**
   * Set the current scenario for score calculation
   */
  setScenario(scenario: Scenario): void {
    this.scenario = scenario;
  }

  /**
   * Calculate the full score breakdown
   *
   * @param decisionTree - Decision tree with visit history
   * @param endingNode - The ending node reached
   * @param options - Additional scoring options
   * @returns Complete calculation result
   */
  calculate(
    decisionTree: DecisionTree,
    endingNode: EndingNode,
    options: ScoreOptions = {}
  ): CalculationResult {
    if (!this.scenario) {
      throw new Error('No scenario set for score calculation');
    }

    const stats = decisionTree.getStats();
    const visitHistory = decisionTree.getVisitHistory();

    // Calculate decision points
    const { decisionPoints, decisionScores } = this.calculateDecisionPoints(
      visitHistory
    );

    // Calculate intelligence points (nodes with intel tags)
    const intelligencePoints = this.calculateIntelligencePoints(visitHistory);

    // Calculate easter egg points
    const easterEggPoints = this.calculateEasterEggPoints(
      options.discoveredEasterEggs ?? []
    );

    // Get base points from ending
    const basePoints = endingNode.score.base;

    // Calculate complexity multiplier
    const complexityMultiplier = calculateComplexityMultiplier(
      stats.uniqueDecisions,
      stats.exploredBranches
    );

    // Get path modifier
    const pathModifier = PATH_MODIFIERS[this.scenario.path];

    // Get outcome multiplier
    const outcomeMultiplier = getOutcomeMultiplier(endingNode.outcomeType);

    // Determine applicable bonuses
    const applicableBonuses = this.determineApplicableBonuses(
      endingNode,
      stats,
      options
    );

    // Determine applicable penalties
    const applicablePenalties = this.determineApplicablePenalties(
      endingNode,
      options
    );

    // Calculate bonus details
    const bonusDetails = applicableBonuses.map((b) => ({
      name: BONUSES[b].name,
      points: BONUSES[b].points,
    }));

    // Calculate penalty details
    const penaltyDetails = applicablePenalties.map((p) => ({
      name: PENALTIES[p.type].name,
      points: PENALTIES[p.type].points * (p.count ?? 1),
    }));

    // Calculate subtotal (before bonuses/penalties)
    const subtotal =
      (basePoints + decisionPoints + intelligencePoints + easterEggPoints) *
      complexityMultiplier *
      pathModifier *
      outcomeMultiplier;

    // Calculate bonus and penalty totals
    const bonusTotal = bonusDetails.reduce((sum, b) => sum + b.points, 0);
    const penaltyTotal = penaltyDetails.reduce((sum, p) => sum + p.points, 0);

    // Calculate final total
    const total = Math.max(0, Math.round(subtotal + bonusTotal + penaltyTotal));

    const breakdown: ScoreBreakdown = {
      basePoints,
      decisionPoints,
      intelligencePoints,
      easterEggPoints,
      complexityMultiplier,
      pathModifier,
      outcomeMultiplier,
      bonuses: bonusDetails,
      penalties: penaltyDetails,
      subtotal: Math.round(subtotal),
      total,
    };

    return {
      breakdown,
      decisionScores,
      applicableBonuses,
      applicablePenalties,
      stats,
    };
  }

  /**
   * Calculate points from individual decisions
   */
  private calculateDecisionPoints(
    visitHistory: NodeVisit[]
  ): { decisionPoints: number; decisionScores: DecisionScore[] } {
    if (!this.scenario) {
      return { decisionPoints: 0, decisionScores: [] };
    }

    const decisionScores: DecisionScore[] = [];
    let totalPoints = 0;

    for (const visit of visitHistory) {
      if (!visit.choiceId) {
        continue;
      }

      const node = this.scenario.nodes[visit.nodeId];
      if (!node) {
        continue;
      }

      // Find the option that was chosen
      if (node.type === 'choice' || node.type === 'multi-select' || node.type === 'timed') {
        const option = node.options.find((opt) => opt.id === visit.choiceId);
        if (option?.score) {
          const points =
            (option.score.base ?? SCORING.baseDecisionPoints) *
            (option.score.multiplier ?? 1);

          decisionScores.push({
            nodeId: visit.nodeId,
            choiceId: visit.choiceId,
            basePoints: option.score.base ?? SCORING.baseDecisionPoints,
            tags: option.score.tags ?? [],
            multiplier: option.score.multiplier ?? 1,
          });

          totalPoints += points;
        } else {
          // Default decision points
          totalPoints += SCORING.baseDecisionPoints;
          decisionScores.push({
            nodeId: visit.nodeId,
            choiceId: visit.choiceId,
            basePoints: SCORING.baseDecisionPoints,
            tags: [],
            multiplier: 1,
          });
        }
      }
    }

    return { decisionPoints: Math.round(totalPoints), decisionScores };
  }

  /**
   * Calculate intelligence gathering points
   */
  private calculateIntelligencePoints(visitHistory: NodeVisit[]): number {
    if (!this.scenario) {
      return 0;
    }

    let points = 0;
    const scoredNodes = new Set<string>();

    for (const visit of visitHistory) {
      if (scoredNodes.has(visit.nodeId)) {
        continue;
      }

      const node = this.scenario.nodes[visit.nodeId];
      if (!node) {
        continue;
      }

      // Check if node has intelligence tags
      if (node.type === 'choice' || node.type === 'multi-select' || node.type === 'timed') {
        for (const option of node.options) {
          if (
            option.score?.tags?.includes('intelligence') ||
            option.score?.tags?.includes('intel')
          ) {
            points += SCORING.intelligenceGatherPoints;
            scoredNodes.add(visit.nodeId);
            break;
          }
        }
      }
    }

    return points;
  }

  /**
   * Calculate easter egg points
   */
  private calculateEasterEggPoints(discoveredEggs: string[]): number {
    if (!this.scenario?.easterEggs) {
      return 0;
    }

    let points = 0;

    for (const eggId of discoveredEggs) {
      const egg = this.scenario.easterEggs.find((e) => e.id === eggId);
      if (egg) {
        points += egg.points;
      }
    }

    return points;
  }

  /**
   * Determine which bonuses apply
   */
  private determineApplicableBonuses(
    ending: EndingNode,
    stats: TreeStats,
    options: ScoreOptions
  ): BonusType[] {
    const bonuses: BonusType[] = [];

    // Peaceful Diplomat - resolved without nuclear weapons
    if (
      ending.outcomeType === 'PEACEFUL_RESOLUTION' ||
      ending.outcomeType === 'MISSION_SUCCESS'
    ) {
      bonuses.push('PEACEFUL_DIPLOMAT');
    }

    // Shadow Operative - covert success
    if (ending.outcomeType === 'MISSION_SUCCESS' && !ending.casualties) {
      bonuses.push('SHADOW_OPERATIVE');
    }

    // Speed Runner - completed under threshold
    if (
      options.includeSpeedBonus &&
      options.playTimeSeconds !== undefined &&
      options.playTimeSeconds < SCORING.speedrunThreshold
    ) {
      bonuses.push('SPEEDRUN');
    }

    // Merciful - minimal casualties
    if (
      ending.casualties !== undefined &&
      ending.casualties === 0 &&
      options.casualties === undefined
    ) {
      bonuses.push('MERCIFUL');
    } else if (options.casualties !== undefined && options.casualties < 1000) {
      bonuses.push('MERCIFUL');
    }

    // De-escalator - lowered DEFCON without conflict
    if (
      (ending.outcomeType === 'PEACEFUL_RESOLUTION' ||
        ending.outcomeType === 'LIMITED_CONVENTIONAL') &&
      !ending.warheadsUsed
    ) {
      bonuses.push('DEESCALATOR');
    }

    // Backdoor Finder - found easter eggs
    if (
      options.discoveredEasterEggs &&
      options.discoveredEasterEggs.length > 0
    ) {
      bonuses.push('BACKDOOR_FINDER');
    }

    // Check ending score bonuses
    if (ending.score.bonuses) {
      for (const bonusName of ending.score.bonuses) {
        const bonusKey = bonusName.toUpperCase().replace(/\s+/g, '_') as BonusType;
        if (bonusKey in BONUSES && !bonuses.includes(bonusKey)) {
          bonuses.push(bonusKey);
        }
      }
    }

    return bonuses;
  }

  /**
   * Determine which penalties apply
   */
  private determineApplicablePenalties(
    ending: EndingNode,
    options: ScoreOptions
  ): { type: PenaltyType; count?: number }[] {
    const penalties: { type: PenaltyType; count?: number }[] = [];

    // Extinction Event - full thermonuclear war
    if (ending.outcomeType === 'FULL_THERMONUCLEAR') {
      penalties.push({ type: 'EXTINCTION_EVENT' });
    }

    // Blown Cover - covert operation compromised
    if (
      ending.outcomeType === 'COMPROMISED' ||
      ending.outcomeType === 'BLOWN_OPERATION'
    ) {
      penalties.push({ type: 'BLOWN_COVER' });
    }

    // Civilian Casualties - per 1000 casualties
    const casualties = options.casualties ?? ending.casualties ?? 0;
    if (casualties >= 1000) {
      const count = Math.floor(casualties / 1000);
      penalties.push({ type: 'CIVILIAN_CASUALTIES', count });
    }

    // Check ending score penalties
    if (ending.score.penalties) {
      for (const penaltyName of ending.score.penalties) {
        const penaltyKey = penaltyName.toUpperCase().replace(/\s+/g, '_') as PenaltyType;
        if (penaltyKey in PENALTIES) {
          const existing = penalties.find((p) => p.type === penaltyKey);
          if (!existing) {
            penalties.push({ type: penaltyKey });
          }
        }
      }
    }

    return penalties;
  }

  /**
   * Calculate quick score without full breakdown
   */
  calculateQuickScore(
    basePoints: number,
    decisionCount: number,
    outcomeType: OutcomeType,
    path: PathType
  ): number {
    const decisionPoints = decisionCount * SCORING.baseDecisionPoints;
    const complexityMultiplier = 1 + decisionCount * 0.05;
    const pathModifier = PATH_MODIFIERS[path];
    const outcomeMultiplier = getOutcomeMultiplier(outcomeType);

    return Math.round(
      (basePoints + decisionPoints) *
        complexityMultiplier *
        pathModifier *
        outcomeMultiplier
    );
  }

  /**
   * Get score rank/tier based on total score
   */
  getScoreRank(score: number): {
    rank: string;
    tier: number;
    description: string;
  } {
    if (score >= 10000) {
      return {
        rank: 'SUPREME COMMANDER',
        tier: 1,
        description: 'Legendary strategic genius',
      };
    } else if (score >= 7500) {
      return {
        rank: 'FOUR-STAR GENERAL',
        tier: 2,
        description: 'Exceptional military leadership',
      };
    } else if (score >= 5000) {
      return {
        rank: 'THREE-STAR GENERAL',
        tier: 3,
        description: 'Superior tactical acumen',
      };
    } else if (score >= 3000) {
      return {
        rank: 'TWO-STAR GENERAL',
        tier: 4,
        description: 'Commendable strategic thinking',
      };
    } else if (score >= 1500) {
      return {
        rank: 'BRIGADIER GENERAL',
        tier: 5,
        description: 'Competent military command',
      };
    } else if (score >= 750) {
      return {
        rank: 'COLONEL',
        tier: 6,
        description: 'Adequate battlefield performance',
      };
    } else if (score >= 300) {
      return {
        rank: 'MAJOR',
        tier: 7,
        description: 'Basic strategic understanding',
      };
    } else if (score >= 100) {
      return {
        rank: 'CAPTAIN',
        tier: 8,
        description: 'Room for improvement',
      };
    } else {
      return {
        rank: 'RECRUIT',
        tier: 9,
        description: 'Beginning of a long journey',
      };
    }
  }

  /**
   * Get outcome description
   */
  getOutcomeDescription(outcome: OutcomeType): string {
    const descriptions: Record<OutcomeType, string> = {
      PEACEFUL_RESOLUTION: 'Crisis resolved through diplomacy',
      LIMITED_CONVENTIONAL: 'Conventional military conflict contained',
      PROXY_CONFLICT: 'Conflict limited to proxy forces',
      TACTICAL_NUCLEAR: 'Tactical nuclear weapons deployed',
      LIMITED_EXCHANGE: 'Limited nuclear exchange occurred',
      MAJOR_EXCHANGE: 'Major nuclear exchange devastated regions',
      FULL_THERMONUCLEAR: 'Global thermonuclear war - civilization ended',
      MISSION_SUCCESS: 'Covert operation completed successfully',
      PARTIAL_SUCCESS: 'Objectives partially achieved',
      COMPROMISED: 'Operation compromised but contained',
      DIPLOMATIC_INCIDENT: 'Diplomatic fallout but no open conflict',
      ESCALATION_TO_CONFLICT: 'Operation escalated to armed conflict',
      BLOWN_OPERATION: 'Complete mission failure',
      HOLLOW_VICTORY: 'Victory at too great a cost',
      STRATEGIC_DEFEAT: 'Strategic position lost',
    };

    return descriptions[outcome];
  }

  /**
   * Compare two scores and return the difference
   */
  compareScores(
    score1: ScoreBreakdown,
    score2: ScoreBreakdown
  ): {
    totalDiff: number;
    percentDiff: number;
    better: 1 | 2 | 0;
  } {
    const totalDiff = score1.total - score2.total;
    const percentDiff =
      score2.total !== 0
        ? Math.round((totalDiff / score2.total) * 100)
        : score1.total > 0
          ? 100
          : 0;

    let better: 1 | 2 | 0 = 0;
    if (totalDiff > 0) {
      better = 1;
    } else if (totalDiff < 0) {
      better = 2;
    }

    return { totalDiff, percentDiff, better };
  }
}
